import React from 'react';
import { Badge, Button } from 'react-bootstrap';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BzmIcon from 'components/icons/BzmIcon';
import { TransactionVersionSummary } from './transactionVersionHistoryTypes';
import { t, formatWhen } from './versionHistoryUtils';
import styles from './TransactionVersionHistoryTab.module.css';

interface Props {
  versions: TransactionVersionSummary[];
  headerContent: React.ReactNode;
  onView: (versionNumber: number) => void;
  onRestore: (versionNumber: number) => void;
}

const TransactionVersionHistoryList: React.FC<Props> = ({ versions, headerContent, onView, onRestore }) => {
  const history = versions.filter(v => !v.current);

  if (history.length === 0) {
    return (
      <div className="bg-light p-2">
        {headerContent}
        <div className="text-center p-4">
          <BzmIcon variant="icon-bzm-timeline" size={64} className={styles.emptyStateIcon} />
          <h4 className="mt-3">{t('noVersions')}</h4>
          <p className="text-muted">{t('noVersionsHint')}</p>
        </div>
      </div>
    );
  }

  const columnDef: BlazeTableColDef[] = [
    {
      header: t('version'),
      accessor: 'versionNumber',
      width: 90,
      cellComponent: (item: TransactionVersionSummary) => (
        <span className="font-weight-bold">v{item.versionNumber}</span>
      ),
    },
    {
      header: t('when'),
      accessor: 'created',
      width: 180,
      cellComponent: (item: TransactionVersionSummary) => formatWhen(item.created),
    },
    {
      header: t('who'),
      accessor: 'createdBy',
      width: 160,
      cellClass: 'text-muted',
    },
    {
      header: t('changes'),
      accessor: 'changedFields',
      cellComponent: (item: TransactionVersionSummary) => (
        <>
          {(item.changedFields || []).map(field => (
            <Badge key={field} variant="light" className="mr-1 border">
              {field}
            </Badge>
          ))}
        </>
      ),
    },
    {
      header: t('actions'),
      accessor: 'versionNumber',
      width: 170,
      cellClass: 'text-right',
      cellComponent: (item: TransactionVersionSummary) =>
        item.current ? (
          <Badge variant="primary">{t('current')}</Badge>
        ) : (
          <>
            <Button variant="secondary" size="sm" className="mr-2" onClick={() => onView(item.versionNumber)}>
              {t('view')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => onRestore(item.versionNumber)}>
              {t('restore')}
            </Button>
          </>
        ),
    },
  ];

  const options: BlazeTableOptions = {
    columnDef,
    selectable: false,
    pageable: false,
    sortable: false,
    showRowExpander: false,
    tableName: 'transactionVersionHistory',
    customRowClassName: (item: TransactionVersionSummary) => (item.current ? styles.currentRow : ''),
  };

  return (
    <div className="bg-light p-2">
      {headerContent}
      <div className="px-4 pt-0">
        <BlazeTable
          options={options}
          data={versions}
          resolveRowKey={(item: TransactionVersionSummary): number => item.versionNumber}
        />
      </div>
    </div>
  );
};

export default TransactionVersionHistoryList;
